import { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { ImageUploadBox, ImageUploader } from '@service/seller/components/ImageUploadBox';
import {
  giftChoiceUnitListDataAtom,
  giftChoiceUnitListStatusAtom,
} from '@shared/api/giftChoiceUnit/giftChoiceUnit.atom';
import { useGiftChoiceUnitListHook } from '@shared/api/giftChoiceUnit/giftChoiceUnit.hook';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import {
  giftItemInfoDataAtomFamily,
  giftItemInfoStatusAtomFamily,
} from '@shared/api/giftItemInfo/giftItemInfo.atom';
import {
  createGiftItemInfo,
  deleteGiftItemInfo,
  updateGiftItemInfo,
} from '@shared/api/giftItemInfo/giftItemInfo.controller';
import { useGiftItemInfoHook } from '@shared/api/giftItemInfo/giftItemInfo.hook';
import {
  GiftItemInfoCreate,
  GiftItemInfoType,
  GiftItemInfoUpdate,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { postImage } from '@shared/api/uploadImage';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useNavigate, useParams } from 'react-router-dom';

interface FormData {
  type: GiftItemInfoType;
  giftItemBrand: string; // 상품 브랜드 ID
  name: string;
  priceRegular: number;
  priceRetail: number;
  priceWhole: number;
  isEnabled: boolean;
  isSelectable: boolean; // 선택형 상품 여부
  detailInfo: string;
  tags: string;
  // 쿠폰 상세
  couponValidPeriod: string;
  couponPlaceOfExchange: string;
  // 주문 설정
  minOrderQuantity: number;
  orderUnit: number;
  order: number | ''; // 표시 순서
}

export const GiftItemInfoFormPage: FC = function GiftItemInfoFormPage() {
  const { itemInfoId } = useParams<{ itemInfoId?: string }>();
  const isEdit = itemInfoId && itemInfoId !== 'new';
  const navigate = useNavigate();

  const { sellerRole } = useAuth();

  // Hooks
  useGiftItemInfoHook(isEdit ? itemInfoId ?? '' : '', sellerRole());
  useGiftItemBrandListHook(sellerRole());
  useGiftChoiceUnitListHook(sellerRole());

  // State
  const giftItemInfoStatus = useRecoilValue(
    giftItemInfoStatusAtomFamily(itemInfoId ?? ''),
  );
  const giftItemInfoData = useRecoilValue(giftItemInfoDataAtomFamily(itemInfoId ?? ''));

  // 브랜드 목록
  const brandListStatus = useRecoilValue(giftItemBrandListStatusAtom);
  const brandList = useRecoilValue(giftItemBrandListDataAtom);

  // 선택유닛 목록
  const choiceUnitListStatus = useRecoilValue(giftChoiceUnitListStatusAtom);
  const choiceUnitList = useRecoilValue(giftChoiceUnitListDataAtom);

  const [formData, setFormData] = useState<FormData>({
    type: GiftItemInfoType.Delivery,
    giftItemBrand: '',
    name: '',
    priceRegular: 0,
    priceRetail: 0,
    priceWhole: 0,
    isEnabled: true,
    isSelectable: false,
    detailInfo: '',
    tags: '',
    couponValidPeriod: '',
    couponPlaceOfExchange: '',
    minOrderQuantity: 1,
    orderUnit: 1,
    order: '',
  });

  // 선택유닛 선택 상태
  const [selectedChoiceUnits, setSelectedChoiceUnits] = useState<string[]>([]);

  // 이미지 관련 state
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [headerImageFiles, setHeaderImageFiles] = useState<File[] | null>(null);
  const [headerImagePreviews, setHeaderImagePreviews] = useState<string[]>([]);
  const [detailImageFiles, setDetailImageFiles] = useState<File[] | null>(null);
  const [detailImagePreviews, setDetailImagePreviews] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);

  // 모달 상태
  const openId = `giftItemInfo-${itemInfoId ?? 'new'}`;
  const headerImageOpenId = `headerImageUploader-${openId}`;
  const setHeaderImageUploaderOpen = useSetRecoilState(
    simpleOpenAtomFamily(`imageUploader-${headerImageOpenId}`),
  );
  const setDetailImageUploaderOpen = useSetRecoilState(
    simpleOpenAtomFamily(`imageUploader-${openId}`),
  );

  // 수정 모드일 때 데이터 로드
  useEffect(() => {
    if (isEdit && giftItemInfoStatus === 'success' && giftItemInfoData) {
      setFormData({
        type: giftItemInfoData.type || GiftItemInfoType.Delivery,
        giftItemBrand:
          typeof giftItemInfoData.giftItemBrand === 'object'
            ? giftItemInfoData.giftItemBrand?._id || ''
            : giftItemInfoData.giftItemBrand || '',
        name: giftItemInfoData.name || '',
        priceRegular: giftItemInfoData.priceRegular || 0,
        priceRetail: giftItemInfoData.priceRetail || 0,
        priceWhole: giftItemInfoData.priceWhole || 0,
        isEnabled: giftItemInfoData.isEnabled ?? true,
        isSelectable: giftItemInfoData.isSelectable ?? false,
        detailInfo: giftItemInfoData.detailInfo || '',
        tags: giftItemInfoData.tags?.join(', ') || '',
        couponValidPeriod: giftItemInfoData.couponDetail?.validPeriod || '',
        couponPlaceOfExchange: giftItemInfoData.couponDetail?.placeOfExchange || '',
        minOrderQuantity: giftItemInfoData.minOrderQuantity || 1,
        orderUnit: giftItemInfoData.orderUnit || 1,
        order: giftItemInfoData.order ?? '',
      });

      // 선택유닛 로드
      if (giftItemInfoData.choiceUnits && Array.isArray(giftItemInfoData.choiceUnits)) {
        setSelectedChoiceUnits(
          giftItemInfoData.choiceUnits.map((unit) =>
            typeof unit === 'object' ? unit._id : unit,
          ),
        );
      }
    }
  }, [isEdit, giftItemInfoStatus, giftItemInfoData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (
      [
        'priceRegular',
        'priceRetail',
        'priceWhole',
        'minOrderQuantity',
        'orderUnit',
      ].includes(name)
    ) {
      setFormData((prev) => ({ ...prev, [name]: Number(value) }));
    } else if (name === 'order') {
      setFormData((prev) => ({ ...prev, [name]: value === '' ? '' : Number(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const onHeaderImageUploaderFunc = async (_data: unknown, files: File[]) => {
    setHeaderImageFiles(files);
    const previews = files.map((file) => URL.createObjectURL(file));
    setHeaderImagePreviews(previews);
    setHeaderImageUploaderOpen(false);
  };

  const onDetailImageUploaderFunc = async (_data: unknown, files: File[]) => {
    setDetailImageFiles(files);
    // 미리보기 생성
    const previews = files.map((file) => URL.createObjectURL(file));
    setDetailImagePreviews(previews);
    setDetailImageUploaderOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      alert('상품명을 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      let imageId: string | undefined;
      let headerImageIds: string[] | undefined;
      let detailImageIds: string[] | undefined;

      // 대표 이미지 업로드
      if (imageFile) {
        const uploadedImage = await postImage(imageFile, sellerRole());
        imageId = uploadedImage._id;
      }

      // 헤더 이미지 업로드 (순서 유지)
      if (headerImageFiles && headerImageFiles.length > 0) {
        headerImageIds = [];
        for (const file of headerImageFiles) {
          const uploaded = await postImage(file, sellerRole());
          headerImageIds.push(uploaded._id);
        }
      }

      // 상세 이미지 업로드 (순서 유지)
      if (detailImageFiles && detailImageFiles.length > 0) {
        detailImageIds = [];
        for (const file of detailImageFiles) {
          const uploaded = await postImage(file, sellerRole());
          detailImageIds.push(uploaded._id);
        }
      }

      const tagsArray = formData.tags
        ? formData.tags
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean)
        : [];

      // 쿠폰 상세 정보
      const couponDetail =
        formData.type === GiftItemInfoType.Coupon &&
        (formData.couponValidPeriod || formData.couponPlaceOfExchange)
          ? {
              validPeriod: formData.couponValidPeriod,
              placeOfExchange: formData.couponPlaceOfExchange,
            }
          : undefined;

      if (isEdit) {
        const updateData: GiftItemInfoUpdate = {
          _id: itemInfoId,
          type: formData.type,
          // 빈 문자열이면 null로 보내서 브랜드 제거
          giftItemBrand: formData.giftItemBrand || null,
          name: formData.name,
          priceRegular: formData.priceRegular,
          priceRetail: formData.priceRetail,
          priceWhole: formData.priceWhole,
          isEnabled: formData.isEnabled,
          isSelectable: formData.isSelectable,
          choiceUnits: formData.isSelectable ? selectedChoiceUnits : [],
          detailInfo: formData.detailInfo || undefined,
          tags: tagsArray.length > 0 ? tagsArray : undefined,
          image: imageId,
          headerImages: headerImageIds,
          detailImages: detailImageIds,
          couponDetail,
          minOrderQuantity: formData.minOrderQuantity,
          orderUnit: formData.orderUnit,
          order: formData.order !== '' ? formData.order : undefined,
        };
        await updateGiftItemInfo(updateData, sellerRole());
        alert('원상품이 수정되었습니다.');
      } else {
        const createData: GiftItemInfoCreate = {
          type: formData.type,
          giftItemBrand: formData.giftItemBrand || undefined,
          name: formData.name,
          priceRegular: formData.priceRegular,
          priceRetail: formData.priceRetail,
          priceWhole: formData.priceWhole,
          isEnabled: formData.isEnabled,
          isSelectable: formData.isSelectable,
          choiceUnits: formData.isSelectable ? selectedChoiceUnits : [],
          detailInfo: formData.detailInfo || undefined,
          tags: tagsArray.length > 0 ? tagsArray : undefined,
          image: imageId,
          headerImages: headerImageIds,
          detailImages: detailImageIds,
          couponDetail,
          minOrderQuantity: formData.minOrderQuantity,
          orderUnit: formData.orderUnit,
          order: formData.order !== '' ? formData.order : undefined,
        };
        await createGiftItemInfo(createData, sellerRole());
        alert('원상품이 등록되었습니다.');
      }
      navigate('/gift-seller/item-info');
    } catch (error) {
      console.error(error);
      alert('저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;

    if (!confirm('정말 삭제하시겠습니까?')) return;

    setLoading(true);
    try {
      await deleteGiftItemInfo(itemInfoId, sellerRole());
      alert('원상품이 삭제되었습니다.');
      navigate('/gift-seller/item-info');
    } catch (error) {
      console.error(error);
      alert('삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 기존 대표 이미지 URL
  const defaultImageUrl = useMemo(() => {
    if (giftItemInfoData?.image && typeof giftItemInfoData.image === 'object') {
      return (giftItemInfoData.image as Image).url;
    }
    return undefined;
  }, [giftItemInfoData]);

  // 기존 헤더 이미지 목록
  const existingHeaderImages = useMemo(() => {
    if (giftItemInfoData?.headerImages && Array.isArray(giftItemInfoData.headerImages)) {
      return giftItemInfoData.headerImages as Image[];
    }
    return [];
  }, [giftItemInfoData]);

  // 기존 상세 이미지 목록
  const existingDetailImages = useMemo(() => {
    if (giftItemInfoData?.detailImages && Array.isArray(giftItemInfoData.detailImages)) {
      return giftItemInfoData.detailImages as Image[];
    }
    return [];
  }, [giftItemInfoData]);

  if (isEdit && giftItemInfoStatus !== 'success') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-800"
          >
            ←
          </button>
          <h1 className="text-xl font-bold">{isEdit ? '원상품 수정' : '원상품 등록'}</h1>
        </div>
        {isEdit && (
          <button
            onClick={handleDelete}
            className="text-sm text-red-500 hover:text-red-700"
            disabled={loading}
          >
            삭제
          </button>
        )}
      </div>

      {/* Form */}
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg bg-white p-6 shadow-sm">
          {/* 기본 정보 섹션 */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* 왼쪽: 기본 정보 */}
            <div className="space-y-4">
              {/* 타입 + 활성화 */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    상품 타입
                  </label>
                  <div className="flex gap-4">
                    <label className="flex cursor-pointer items-center gap-1.5">
                      <input
                        type="radio"
                        name="type"
                        value={GiftItemInfoType.Delivery}
                        checked={formData.type === GiftItemInfoType.Delivery}
                        onChange={handleChange}
                        className="h-4 w-4"
                      />
                      <span className="text-sm">배송형</span>
                    </label>
                    <label className="flex cursor-pointer items-center gap-1.5">
                      <input
                        type="radio"
                        name="type"
                        value={GiftItemInfoType.Coupon}
                        checked={formData.type === GiftItemInfoType.Coupon}
                        onChange={handleChange}
                        className="h-4 w-4"
                      />
                      <span className="text-sm">쿠폰형</span>
                    </label>
                  </div>
                </div>
                <label className="flex cursor-pointer items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">활성화</span>
                  <input
                    type="checkbox"
                    name="isEnabled"
                    checked={formData.isEnabled}
                    onChange={handleChange}
                    className="h-4 w-4"
                  />
                </label>
              </div>

              {/* 상품 브랜드 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  상품 브랜드
                </label>
                <select
                  name="giftItemBrand"
                  value={formData.giftItemBrand}
                  onChange={handleChange}
                  className="w-full rounded border px-3 py-2 text-sm"
                  disabled={brandListStatus !== 'success'}
                >
                  <option value="">선택 안함</option>
                  {brandList.map((brand) => (
                    <option key={brand._id} value={brand._id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
                <p className="mt-0.5 text-xs text-gray-400">
                  예: 스타벅스, 투썸플레이스 (선택 안함으로 브랜드 제거 가능)
                </p>
              </div>

              {/* 선택형 상품 */}
              <div className="rounded border bg-indigo-50 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-indigo-700">
                      선택형 상품
                    </label>
                    <p className="text-xs text-indigo-500">
                      고객이 교환 시 원하는 옵션을 선택할 수 있습니다
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    name="isSelectable"
                    checked={formData.isSelectable}
                    onChange={handleChange}
                    className="h-5 w-5"
                  />
                </div>

                {/* 선택유닛 선택 (isSelectable일 때만) */}
                {formData.isSelectable && (
                  <div className="mt-3 border-t border-indigo-200 pt-3">
                    <label className="mb-2 block text-sm font-medium text-indigo-700">
                      선택 가능한 유닛
                    </label>
                    {choiceUnitListStatus === 'success' && choiceUnitList.length > 0 ? (
                      <div className="max-h-48 space-y-2 overflow-y-auto">
                        {choiceUnitList.map((unit) => {
                          const isSelected = selectedChoiceUnits.includes(unit._id);
                          return (
                            <label
                              key={unit._id}
                              className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2 ${
                                isSelected
                                  ? 'border-indigo-400 bg-indigo-100'
                                  : 'border-gray-200 bg-white hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedChoiceUnits((prev) => [...prev, unit._id]);
                                  } else {
                                    setSelectedChoiceUnits((prev) =>
                                      prev.filter((id) => id !== unit._id),
                                    );
                                  }
                                }}
                                className="h-4 w-4"
                              />
                              {unit.image && typeof unit.image === 'object' && (
                                <img
                                  src={fileBucketUrl(unit.image.url, 'w200')}
                                  alt={unit.name}
                                  className="h-8 w-8 rounded object-cover"
                                />
                              )}
                              <div className="flex-1">
                                <p className="text-sm font-medium">{unit.name}</p>
                                {unit.giftItemBrand &&
                                  typeof unit.giftItemBrand === 'object' && (
                                    <span className="text-xs text-gray-500">
                                      {unit.giftItemBrand.name}
                                    </span>
                                  )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ) : choiceUnitListStatus === 'loading' ? (
                      <p className="text-xs text-gray-500">로딩 중...</p>
                    ) : (
                      <p className="text-xs text-gray-500">
                        등록된 선택유닛이 없습니다.{' '}
                        <a
                          href="/gift-seller/choice-unit/new"
                          className="text-indigo-600 underline"
                        >
                          선택유닛 등록
                        </a>
                      </p>
                    )}
                    {selectedChoiceUnits.length > 0 && (
                      <p className="mt-2 text-xs text-indigo-600">
                        {selectedChoiceUnits.length}개 선택됨
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* 상품명 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  상품명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="상품명을 입력하세요"
                  className="w-full rounded border px-3 py-2 text-sm"
                  required
                />
              </div>

              {/* 가격 설정 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  가격 설정 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">정가</label>
                    <input
                      type="number"
                      name="priceRegular"
                      value={formData.priceRegular}
                      onChange={handleChange}
                      placeholder="0"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">판매가</label>
                    <input
                      type="number"
                      name="priceRetail"
                      value={formData.priceRetail}
                      onChange={handleChange}
                      placeholder="0"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">도매가</label>
                    <input
                      type="number"
                      name="priceWhole"
                      value={formData.priceWhole}
                      onChange={handleChange}
                      placeholder="0"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* 주문 설정 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  주문 설정
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">
                      최소주문수량
                    </label>
                    <input
                      type="number"
                      name="minOrderQuantity"
                      value={formData.minOrderQuantity}
                      onChange={handleChange}
                      min={1}
                      placeholder="1"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">주문단위</label>
                    <input
                      type="number"
                      name="orderUnit"
                      value={formData.orderUnit}
                      onChange={handleChange}
                      min={1}
                      placeholder="1"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                    />
                    <p className="mt-0.5 text-[10px] text-gray-400">(수량 증감 단위)</p>
                  </div>
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">표시순서</label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleChange}
                      min={0}
                      placeholder="미설정"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                    />
                    <p className="mt-0.5 text-[10px] text-gray-400">
                      (낮을수록 앞에 표시)
                    </p>
                  </div>
                </div>
              </div>

              {/* 태그 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  태그
                </label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  placeholder="쉼표로 구분 (예: 답례품, 인기상품)"
                  className="w-full rounded border px-3 py-2 text-sm"
                />
              </div>

              {/* 쿠폰 상세 정보 (쿠폰형일 때만) */}
              {formData.type === GiftItemInfoType.Coupon && (
                <div className="rounded border bg-purple-50 p-3">
                  <label className="mb-2 block text-sm font-medium text-purple-700">
                    쿠폰 상세 정보
                  </label>
                  <div className="space-y-2">
                    <div>
                      <label className="mb-0.5 block text-xs text-gray-600">
                        유효기간
                      </label>
                      <input
                        type="text"
                        name="couponValidPeriod"
                        value={formData.couponValidPeriod}
                        onChange={handleChange}
                        placeholder="예: 발행일로부터 3개월"
                        className="w-full rounded border px-2 py-1.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-0.5 block text-xs text-gray-600">교환처</label>
                      <input
                        type="text"
                        name="couponPlaceOfExchange"
                        value={formData.couponPlaceOfExchange}
                        onChange={handleChange}
                        placeholder="예: 전국 CU 편의점"
                        className="w-full rounded border px-2 py-1.5 text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* 상세 설명 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  상세 설명
                </label>
                <textarea
                  name="detailInfo"
                  value={formData.detailInfo}
                  onChange={handleChange}
                  placeholder="상세 설명을 입력하세요"
                  className="h-24 w-full rounded border px-3 py-2 text-sm"
                />
              </div>
            </div>

            {/* 오른쪽: 이미지 */}
            <div className="space-y-4">
              {/* 대표 이미지 */}
              <div className="max-w-[400px]">
                <ImageUploadBox
                  title="대표 이미지"
                  setFile={setImageFile}
                  defaultImageUrl={defaultImageUrl}
                />
              </div>

              {/* 헤더 이미지 (정사각형, carousel용) */}
              <div className="max-w-[400px]">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  헤더 이미지 (정사각형, carousel용)
                </label>
                <p className="mb-1 text-xs text-gray-500">
                  상세페이지 상단에 스크롤 형태로 표시됩니다. 순서대로 정렬됩니다.
                </p>
                <Button
                  type="button"
                  className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white"
                  onClick={() => {
                    setHeaderImageUploaderOpen(true);
                  }}
                >
                  이미지 업로드
                </Button>

                {/* 미리보기 또는 기존 이미지 */}
                {headerImagePreviews.length > 0 ? (
                  <div className="mt-2 flex gap-2 overflow-x-auto">
                    {headerImagePreviews.map((preview, idx) => (
                      <div key={idx} className="relative flex-shrink-0">
                        <img
                          src={preview}
                          alt={`헤더이미지 ${idx + 1}`}
                          className="h-20 w-20 rounded object-cover"
                        />
                        <span className="absolute left-1 top-1 rounded bg-black/50 px-1 text-xs text-white">
                          {idx + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : existingHeaderImages.length > 0 ? (
                  <div className="mt-2 flex gap-2 overflow-x-auto">
                    {existingHeaderImages.map((img, idx) => (
                      <div key={idx} className="relative flex-shrink-0">
                        <img
                          src={fileBucketUrl(img.url, 'w200')}
                          alt={`헤더이미지 ${idx + 1}`}
                          className="h-20 w-20 rounded object-cover"
                        />
                        <span className="absolute left-1 top-1 rounded bg-black/50 px-1 text-xs text-white">
                          {idx + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-gray-400">
                    등록된 헤더 이미지가 없습니다.
                  </p>
                )}
              </div>

              {/* 상세 이미지 */}
              <div className="max-w-[400px]">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  상세 이미지 (세로로 이어붙임)
                </label>
                <p className="mb-1 text-xs text-gray-500">
                  상세페이지 하단에 순서대로 세로로 표시됩니다.
                </p>
                <Button
                  type="button"
                  className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white"
                  onClick={() => setDetailImageUploaderOpen(true)}
                >
                  이미지 업로드
                </Button>

                {/* 미리보기 또는 기존 이미지 */}
                {detailImagePreviews.length > 0 ? (
                  <div className="mt-2 space-y-1">
                    {detailImagePreviews.map((preview, idx) => (
                      <div key={idx} className="relative">
                        <img
                          src={preview}
                          alt={`상세이미지 ${idx + 1}`}
                          className="w-full rounded object-cover"
                        />
                        <span className="absolute left-1 top-1 rounded bg-black/50 px-1 text-xs text-white">
                          {idx + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : existingDetailImages.length > 0 ? (
                  <div className="mt-2 space-y-1">
                    {existingDetailImages.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img
                          src={fileBucketUrl(img.url, 'w400')}
                          alt={`상세이미지 ${idx + 1}`}
                          className="w-full rounded object-cover"
                        />
                        <span className="absolute left-1 top-1 rounded bg-black/50 px-1 text-xs text-white">
                          {idx + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-gray-400">
                    등록된 상세 이미지가 없습니다.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* 제출 버튼 */}
          <div className="mt-6 border-t pt-4">
            <Button
              type="button"
              disabled={loading}
              onClick={handleSubmit}
              className="rounded bg-blue-600 px-6 py-2 font-medium text-white disabled:bg-gray-400"
            >
              {loading ? '저장 중...' : isEdit ? '수정하기' : '등록하기'}
            </Button>
          </div>
        </div>
      </div>

      {/* 헤더 이미지 업로더 모달 */}
      <ImageUploader
        openId={headerImageOpenId}
        title="헤더 이미지 업로드 (정사각형)"
        onSubmit={onHeaderImageUploaderFunc}
        useName={false}
      />

      {/* 상세 이미지 업로더 모달 */}
      <ImageUploader
        openId={openId}
        title="상세이미지 업로드"
        onSubmit={onDetailImageUploaderFunc}
        useName={false}
      />
    </div>
  );
};
